<template>
  <router-link
    :to="{ name: 'reward-details', params: { id: reward.id } }"
    :class="['bounty', {'compact': compact}]"
    :style="cardCustomStyle"
  >
    <div
      :class="['image', {'blank': !reward.image}]"
      :style="imageContainerCustomStyle"
    >
      <img
        v-if="reward.image"
        :src="reward.image"
        :style="imageCustomStyle"
        alt=""
      />
    </div>
    <div class="info">
      <span
        class="bounty-title"
        :style="titleCustomStyle"
      >
        {{ reward.name }}
      </span>
      <div
        v-if="reward.contest && !hideProgress"
        class="points"
        :style="pointsCustomStyle"
      >
        <span v-if="reward.is_participating">
          {{$t('bounties.youAreParticipating')}}
        </span>
        <span
          v-else-if="reward.point_contest && reward.needed_points == 0"
          class="caption"
        >
          {{ $tc(
            'bounties.neededPointsRequirementText',
            0,
            { points: 0 }
          ) }}
        </span>
        <span v-else-if="reward.point_contest">
          <strong>
            {{ $t(
              'bounties.redeemBountyPointsText',
              { count: customerRelationshipPoints.toLocaleString() }
            ) }}
          </strong> / {{ reward.needed_points.toLocaleString() }}
        </span>
        <span v-else-if="reward.cr_rank && reward.show_ranking">
          {{ $t(
            'bounties.contestDetailScreenRanking',
            { rank: reward.cr_rank }
          ) }}
        </span>
        <div
          v-if="reward.show_progress_bar && !reward.is_participating && !reward.show_ranking"
          class="progress"
        >
          <v-progress-linear :value="progress" v-bind="{...defaultProgressStyle, ...progressCustomStyle}" />
        </div>
      </div>
      <div
        v-if="!reward.contest && !hideProgress"
        class="points"
        :style="pointsCustomStyle"
      >
        <span v-if="reward.needed_points == 0" class="caption">
          {{ $tc(
            'bounties.neededPointsRequirementText',
            0,
            { points: 0 }
          ) }}
        </span>
        <span
          v-else-if="reward.is_redeemable || reward.is_activateable"
        >
          {{ $t(
            'bounties.redeemBountyPointsText',
            { count: reward.needed_points.toLocaleString() }
          ) }}
        </span>
        <span v-else>
          <strong>
            {{ $t(
              'bounties.redeemBountyPointsText',
              { count: customerRelationshipPoints.toLocaleString() }
            ) }}
          </strong>/{{ reward.needed_points.toLocaleString() }}
        </span>
        <div
          v-if="reward.show_progress_bar && !reward.is_participating && !reward.show_ranking"
          class="progress"
        >
          <v-progress-linear :value="progress" v-bind="{...defaultProgressStyle, ...progressCustomStyle}" />
        </div>
      </div>
    </div>
  </router-link>
</template>

<script>
import _ from 'lodash'

export default {
  name: "Bounty",
  props: {
    bounty: {
      type: Object,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    customStyle: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      defaultProgressStyle: {
        color: 'primary',
        height: 8,
        rounded: true,
        'background-color': '#E5E8F0',
      }
    }
  },
  computed: {
    reward() {
      return this.bounty
    },
    customerRelationshipPoints() {
      let crp = _.defaultTo(_.get(this.bounty, 'cr_points'), 0)
      return (crp < this.bounty.needed_points)
        ? crp
        : this.bounty.needed_points
    },
    progress() {
      let crp = this.customerRelationshipPoints
      if (this.bounty.needed_points === 0) {
        return 100
      }
      return (crp < this.bounty.needed_points)
        ? Math.ceil(crp * 100 / this.bounty.needed_points)
        : 100
    },
    hideProgress() {
      const neededPoints = _.get(this.bounty, 'needed_points', 0)

      return neededPoints === 0 || _.isNull(neededPoints)
    },
    cardCustomStyle() {
      return _.get(this.customStyle, 'card', null)
    },
    titleCustomStyle() {
      return _.get(this.customStyle, 'title', null)
    },
    pointsCustomStyle() {
      return _.get(this.customStyle, 'points', null)
    },
    imageContainerCustomStyle() {
      return _.get(this.customStyle, 'image', null)
    },
    imageCustomStyle() {
      return _.get(this.customStyle, 'image_inner', null)
    },
    progressCustomStyle() {
      return _.get(this.customStyle, 'progress', null)
    }
  }
}
</script>

<style lang="scss" scoped>
.bounty {
  border-radius: 0;
  overflow: hidden;
  border: 1px solid #EFEFEF;
  box-shadow: 0px 4px 6px rgba(0,0,0,0.10);
  color: var(--text);
  display: grid;
  grid-template-rows: min-content 1fr;
  position: relative;
  transition: box-shadow .3s ease-out;
  will-change: box-shadow;
  // max-width: 286.5px;
  display: block;

  .image {
    overflow: hidden;
    position: relative;

    &.blank {
      background: linear-gradient(45deg, #3A434B, rgb(132, 148, 163)) ;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      display: block;
      backface-visibility: hidden;
      transform: scale(1) translateZ(0);
      transition: transform .3s ease-out;
    }
  }

  &:hover .image img {
    transform: scale(1.04) translateZ(0);
  }

  .info {
    padding: 20px 15px 22px;
    font-size: 14px;
    display: grid;
    align-self: stretch;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr min-content;
    gap: 12px;
  }

  .bounty-title {
    font-size: 17px;
    font-weight: 600;
    line-height: 1.3;
  }

  .points {
    color: var(--text);
    font-size: 14px;
  }

  .progress {
    margin-top: 6px;
  }
}

.bounty:not(.compact) {
  height: 100%;

  .image {
    width: 100% !important;
    max-height: 200px;
  }
}

.bounty.compact {
  display: flex;
  padding: 10px;
  max-width: none;

  .image {
    width: 100%;
    max-width: 100px;
    height: auto;
    border-radius: var(--border-radius);
  }

  .info {
    flex: 1;
    padding: 0 16px;
    display: flex;
    justify-content: center;
    flex-direction: column;
  }

  .points {
    margin: 0;
  }
}

@media(min-width: 460px) {
  .bounty.compact {
    .image {
      max-width: 140px;
    }
  }
}
</style>
